import React, { useState } from 'react';
import { Filter, X, Check } from 'lucide-react';

/**
 * Advanced filter panel — collapsible, with multi-status + date range + active count badge.
 *
 * Props:
 *  - statuses: string[]     — list of all possible statuses
 *  - selectedStatuses: Set  — current selection
 *  - setSelectedStatuses: (set) => void
 *  - statusStyles: {status: css}  — same as STATUS_STYLES of the parent
 *  - dateFrom / dateTo: string (ISO yyyy-mm-dd)
 *  - setDateFrom / setDateTo
 *  - testId: prefix for data-testid
 */
export default function AdvancedFilterPanel({
  statuses = [],
  selectedStatuses,
  setSelectedStatuses,
  statusStyles = {},
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  testId = 'advfilter',
  minAmount, setMinAmount,
  maxAmount, setMaxAmount,
}) {
  const [open, setOpen] = useState(false);
  const activeCount =
    (selectedStatuses?.size || 0) +
    (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) +
    (minAmount ? 1 : 0) + (maxAmount ? 1 : 0);

  const toggleStatus = (s) => {
    const next = new Set(selectedStatuses);
    next.has(s) ? next.delete(s) : next.add(s);
    setSelectedStatuses(next);
  };
  const clearAll = () => {
    setSelectedStatuses(new Set());
    setDateFrom?.(''); setDateTo?.('');
    setMinAmount?.(''); setMaxAmount?.('');
  };

  return (
    <div className="relative">
      <button
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className={`h-10 px-3.5 rounded-xl bg-white border text-[13px] font-medium flex items-center gap-2 transition-colors ${activeCount > 0 ? 'border-[#4F46E5] text-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6] text-[#4A4B63] hover:border-[#C7D2FE]'}`}
      >
        <Filter className="w-4 h-4" />
        Filtres
        {activeCount > 0 && (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#4F46E5] text-white text-[10.5px] font-bold">{activeCount}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute left-0 mt-2 w-[380px] bg-white rounded-2xl shadow-xl border border-[#EFF0F6] p-4 z-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[13px] font-bold text-[#1A1B2E] uppercase tracking-wide">Filtres avancés</h4>
              {activeCount > 0 && (
                <button onClick={clearAll} data-testid={`${testId}-clear`} className="text-[11.5px] text-[#B91C1C] font-semibold hover:underline">Tout effacer</button>
              )}
            </div>

            {statuses.length > 0 && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wider mb-2">Statuts</div>
                <div className="flex flex-wrap gap-1.5">
                  {statuses.map((s) => {
                    const active = selectedStatuses?.has(s);
                    const baseStyle = statusStyles[s] || 'bg-[#F3F4F8] text-[#4A4B63]';
                    return (
                      <button
                        key={s}
                        data-testid={`${testId}-status-${s}`}
                        onClick={() => toggleStatus(s)}
                        className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11.5px] font-semibold transition-all ${active ? baseStyle + ' ring-2 ring-[#4F46E5] ring-offset-1' : baseStyle + ' opacity-60 hover:opacity-100'}`}
                      >
                        {active && <Check className="w-3 h-3" />}
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(setDateFrom || setDateTo) && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wider mb-2">Plage de dates</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10.5px] text-[#8E8FA5] block mb-1">Du</label>
                    <input type="date" value={dateFrom || ''} onChange={(e) => setDateFrom?.(e.target.value)} data-testid={`${testId}-date-from`} className="w-full h-9 px-2.5 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
                  </div>
                  <div>
                    <label className="text-[10.5px] text-[#8E8FA5] block mb-1">Au</label>
                    <input type="date" value={dateTo || ''} onChange={(e) => setDateTo?.(e.target.value)} data-testid={`${testId}-date-to`} className="w-full h-9 px-2.5 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
                  </div>
                </div>
              </div>
            )}

            {(setMinAmount || setMaxAmount) && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wider mb-2">Montant (€)</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10.5px] text-[#8E8FA5] block mb-1">Min</label>
                    <input type="number" min="0" step="1" value={minAmount || ''} onChange={(e) => setMinAmount?.(e.target.value)} data-testid={`${testId}-amount-min`} placeholder="0" className="w-full h-9 px-2.5 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
                  </div>
                  <div>
                    <label className="text-[10.5px] text-[#8E8FA5] block mb-1">Max</label>
                    <input type="number" min="0" step="1" value={maxAmount || ''} onChange={(e) => setMaxAmount?.(e.target.value)} data-testid={`${testId}-amount-max`} placeholder="∞" className="w-full h-9 px-2.5 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-3 border-t border-[#EFF0F6] -mx-4 -mb-4 px-4 pb-3">
              <button onClick={() => setOpen(false)} data-testid={`${testId}-close`} className="h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold hover:bg-[#EDE8FF] flex items-center gap-1">
                <X className="w-3.5 h-3.5" /> Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
